import { useEffect } from "react";
import styled from "styled-components";
import { Controller, useForm } from 'react-hook-form';
import { useToasts } from "react-toast-notifications";
import DialogModal from "components/Modal/Dialog";
import UniversalInput from "components/Input/UniversalInput";
import EmpEditHeader from "../../editHeader";
import { createClassification, getClassificationList } from "services";
import SelectWithAdd from "components/Dropdowns/SelectWithAdd";
import DatePicker from "components/DatePickers/DatePicker";
import { utcToZonedTime } from 'date-fns-tz';

const DriversInformationEdit = (props: any) => {
  const { addToast } = useToasts();

  const { register, handleSubmit, setValue, setError, control, reset, formState: { errors } } = useForm({
    shouldFocusError: false,
    defaultValues: {
      number: null,
      classification: null,
      region: null,
      expiration_date: ''
    }
  });

  const { user, jobData, chosenLicense, editMode } = props;

  const onSubmit = (data: any) => {
    props.onSubmit(data);
  };

  useEffect(() => {
    if (chosenLicense && editMode) {
      setValue('number', chosenLicense.number ?? null);
      setValue('classification', chosenLicense.employee_driver_classifications[0]?.classification ?? null);
      setValue('region', chosenLicense.region ?? null);
      setValue('expiration_date', chosenLicense.expiration_date && utcToZonedTime(new Date(chosenLicense.expiration_date), 'UTC'));
    } else reset()
  }, [chosenLicense, editMode, props.isOpen])

  useEffect(() => {
    if (props.editErrors) {
      props.editErrors.map((item: any) => setError(item.field, { type: 'string', message: item.message }))
    }
  }, [props.editErrors]);

  const onError = (err: any) => {
    if (err) {
      addToast(<ToastContentContainer>
        <b>There were some errors with your form.</b> Please Fix The Highlighted Fields Below
      </ToastContentContainer>, {
        appearance: 'error',
        autoDismiss: true,
        placement: 'top-center'
      });
    }
  };

  return (
    <DialogModal
      open={props.isOpen}
      title={`${editMode ? 'Edit' : 'Add'} Driver License`}
      onClose={() => { props.onModalClose() }}
      actionButton={handleSubmit(onSubmit, onError)}
      withButtons
      cancelButtonText='CANCEL'
      actionButtonText='SAVE'
      actionLoading={props.loadingRequest}
      nominalHeader={
        <EmpEditHeader
          employeeName={`${user.first_name} ${user.last_name}`}
          avatarUuid={user.uuid}
          employeeId={user.id}
          jobData={jobData}
        />
      }
      fullWidth
    >
      <Wrapper>
        <form>
          <div className='body'>
            <div className='contact-section'>
              <div className='input-item'>
                <label>License #<sup>*</sup></label>
                <UniversalInput
                  errorText={
                    errors.number ? 'License # is required' : ""
                  }
                  {...register("number", { required: true })}
                />
              </div>

              <div className='input-item'>
                <label>Classification<sup>*</sup></label>
                <Controller
                  name="classification"
                  control={control}
                  rules={{ required: true }}
                  render={({ field: { value, onChange } }) => (
                    <SelectWithAdd
                      name='classification'
                      inputPlaceholder='Select Classification'
                      inputValue={value}
                      loadRemoteData={() => getClassificationList(100, 1)}
                      createRequest={createClassification}
                      onChange={onChange}
                      errorText={errors.classification ? 'Classification is required' : ""}
                    />
                  )}
                />
              </div>

              <div className='input-item'>
                <label>State / Region / Province</label>
                <UniversalInput
                  {...register("region")}
                />
              </div>

              <div className='input-item' style={{ width: 200 }}>
                <Controller
                  name="expiration_date"
                  control={control}
                  rules={{ validate: value => value !== null || 'Please enter a valid date' }}
                  render={({ field: { onChange, value } }) => (
                    <DatePicker
                      selected={value}
                      onChange={onChange}
                      label='Expiration'
                      errorText={errors.expiration_date ? errors.expiration_date.message : ''}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </form>
      </Wrapper>
    </DialogModal>
  );
};

export default DriversInformationEdit;

const ToastContentContainer = styled.div`
    & > b {
        font-family: 'Aspira Demi';
    }
`;

const Wrapper = styled.div`
  .body{
     .input-item{
         max-width: 416px;
         margin-bottom: 21px;
         
         & > label{
           display: inline-block;
           margin-bottom: 6px;
           & > sup {
              color: #C54343;
            }
         }
     }
  }
`;
